import { APP_NAME, PORTAL_NAME } from '@/lib/branding'
import { CHARGEE_ENTITY as C, type TermsDocument } from './types'

/**
 * Aanvullende voorwaarden voor de pilot Net bewust thuis laden, tweede revisie
 * van 30 oktober 2026: een laadpaal is niet meer verplicht, aanmelden in de
 * eerste vijf dagen van een maand telt voor die maand, en de Beloning is per
 * maand. Ze beschrijven wat de software ook echt doet (aanmelden,
 * start, controle, stoppen, beloning, misbruik), zodat tekst en gedrag niet
 * uit elkaar lopen. Een wijziging hier is een nieuwe revisie met een nieuwe
 * versie, ook in api/src/programs/definitions.ts.
 */
export const PROGRAM_TERMS: TermsDocument = {
  title: 'Voorwaarden van Net bewust thuis laden',
  version: '2026-10-30.2',
  revisionDate: '2026-10-30',
  intro: [
    `Deze voorwaarden gelden voor deelname aan de pilot Net bewust thuis laden van ${C.name} Ze vullen de Algemene Voorwaarden van Chargee en de Privacyverklaring van Chargee aan, waarmee je al akkoord bent gegaan. Lees ze goed voordat je je aanmeldt.`,
    'Het delen van gegevens met Netbeheer Nederland voor deze pilot staat in een apart document, waarvoor we je apart om toestemming vragen.',
  ],
  articles: [
    {
      id: 'wie-zijn-wij',
      title: 'Wie wij zijn en waar deze voorwaarden bij horen',
      blocks: [
        `De pilot wordt georganiseerd door ${C.name}, statutair gevestigd in ${C.seat}, kantoorhoudend aan de ${C.address}, ingeschreven bij de Kamer van Koophandel onder nummer ${C.kvk} ("Chargee", "wij").`,
        'Deze voorwaarden zijn aanvullende voorwaarden bij de Algemene Voorwaarden van Chargee. Alles wat hierin niet anders is geregeld, valt onder de Algemene Voorwaarden. Zijn deze voorwaarden en de Algemene Voorwaarden over de pilot met elkaar in strijd, dan gaan deze voorwaarden voor.',
        'Voor de verwerking van je persoonsgegevens geldt de Privacyverklaring van Chargee, aangevuld met de Voorwaarden voor het delen van gegevens met Netbeheer Nederland.',
      ],
    },
    {
      id: 'begrippen',
      title: 'Begrippen',
      blocks: [
        [
          'Pilot: het programma Net bewust thuis laden, zoals beschreven in deze voorwaarden.',
          'Deelnemer of jij: de persoon die zich met een Chargee-account voor een Adres aanmeldt voor de Pilot.',
          'Adres: het woonadres in Nederland waarvoor je deelneemt, zoals bekend in je Chargee-account.',
          'Apparaten: alle elektrische auto’s en, als die er zijn, alle laadpalen die op het moment van aanmelden aan het Adres zijn gekoppeld en verbonden, en de Sparky of Flint die de slimme meter (P1-poort) op het Adres uitleest.',
          'Piekvenster: elke dag van 16:00 tot 21:00 uur Nederlandse tijd.',
          'Pilotperiode: van 1 november 2026 om 00:00 uur tot 1 maart 2027 om 00:00 uur Nederlandse tijd.',
          'Thuis laden: het opladen van een elektrische auto op het Adres, met een gekoppelde laadpaal, een andere laadpaal, een stopcontact of op een andere manier.',
          'Beloning: de eenmalige vergoeding voor deelname zoals beschreven in artikel 9.',
          `${PORTAL_NAME}: de online omgeving waar je je aanmeldt, je deelname bekijkt en kunt stoppen.`,
        ],
      ],
    },
    {
      id: 'wie-kan-meedoen',
      title: 'Wie kan meedoen',
      blocks: [
        'Je kunt meedoen als je aan alle volgende voorwaarden voldoet, zowel bij aanmelding als gedurende je deelname:',
        [
          'je bent 18 jaar of ouder en hebt een Chargee-account;',
          'het Adres ligt in Nederland en heeft een geldige Nederlandse postcode;',
          'je woont op het Adres of mag namens de bewoners van het Adres beslissen over het laden van de auto’s op dat Adres;',
          `aan het Adres zijn ten minste één elektrische auto en een Sparky of Flint op de slimme meter gekoppeld in de ${APP_NAME}, en deze zijn verbonden. Een laadpaal is niet verplicht; is er een laadpaal gekoppeld en verbonden, dan doet die als Apparaat mee;`,
          'je gaat akkoord met deze voorwaarden en geeft toestemming voor het delen van gegevens met Netbeheer Nederland.',
        ],
        'Per Adres kan één Deelnemer meedoen, ook als meerdere accounts toegang hebben tot dat Adres. Een Apparaat telt voor één Adres: een auto, laadpaal, Sparky of Flint die al meedoet voor een ander Adres, telt niet nog een keer.',
        'Je kunt met meerdere Adressen meedoen, mits elk Adres afzonderlijk aan deze voorwaarden voldoet en elk zijn eigen Apparaten heeft.',
      ],
    },
    {
      id: 'aanmelden',
      title: 'Aanmelden en start van je deelname',
      blocks: [
        `Je meldt je aan via ${PORTAL_NAME}. Voordat je je aanmelding bevestigt, zie je per Apparaat of het verbonden is, op welke datum je deelname start en hoe hoog je Beloning is.`,
        'Je deelname start op de eerste dag van de kalendermaand na je aanmelding, maar niet eerder dan 1 november 2026. Meld je je aan in de eerste vijf dagen van een kalendermaand (tot de zesde om 00:00 uur), dan start je deelname direct bij je aanmelding en doe je die kalendermaand nog mee. Meld je je bijvoorbeeld aan in oktober 2026, dan start je op 1 november 2026; meld je je aan op 3 november, dan start je direct; meld je je aan op 15 november, dan start je op 1 december 2026.',
        'Aanmelden kan tot 6 februari 2027 om 00:00 uur.',
        'Bij aanmelding leggen we vast welke Apparaten op dat moment aan het Adres gekoppeld en verbonden zijn. Die Apparaten doen mee. Koppel je later een auto of laadpaal aan het Adres, dan geldt artikel 5 ook voor die auto of laadpaal.',
        'Chargee kan een aanmelding weigeren als niet aan artikel 3 wordt voldaan of als er aanwijzingen zijn voor misbruik zoals bedoeld in artikel 10.',
      ],
    },
    {
      id: 'jouw-afspraak',
      title: 'Wat je afspreekt',
      blocks: [
        'Vanaf de startdatum van je deelname tot het einde van de Pilotperiode, of tot het einde van je deelname als dat eerder is:',
        [
          `laad je geen enkele elektrische auto thuis tijdens het Piekvenster, op welke manier dan ook. Dit geldt voor alle auto’s die je op het Adres laadt, ook als ze niet in de ${APP_NAME} zijn gekoppeld;`,
          `houd je alle Apparaten gekoppeld in de ${APP_NAME} en verbonden, zodat Chargee je deelname kan controleren;`,
          'ontkoppel, verplaats of schakel je Apparaten niet uit om te voorkomen dat laden in het Piekvenster zichtbaar wordt.',
        ],
        'Laden buiten het Piekvenster, en laden buiten het Adres (bijvoorbeeld bij je werk of onderweg), is gewoon toegestaan.',
        'Je bent zelf verantwoordelijk voor het instellen van je laadpaal, auto of laadschema zodat er in het Piekvenster niet wordt geladen. Een laadschema van Chargee of een derde dat in het Piekvenster laadt, telt als thuis laden.',
      ],
    },
    {
      id: 'controle',
      title: 'Hoe we je deelname controleren',
      blocks: [
        'Chargee controleert gedurende de Pilotperiode automatisch of je je aan artikel 5 houdt. Daarvoor gebruiken we:',
        [
          'de laadsessies en laadstatus van de gekoppelde auto’s en, als die er zijn, de gekoppelde laadpalen;',
          'de meetgegevens van de slimme meter via Sparky of Flint, waaruit we kunnen afleiden of er in het Piekvenster een laadstroom wordt afgenomen, ook via een laadpaal of stopcontact dat niet is gekoppeld;',
          'de verbindingsstatus van de Apparaten.',
        ],
        'In Mijn programma’s zie je de status van je deelname en van je Apparaten. Verliest een Apparaat zijn verbinding, dan laten we je dat weten, zodat je de verbinding kunt herstellen.',
        'De gegevens die we hiervoor gebruiken, hoe lang we ze bewaren en welke rechten je hebt, staan in de Privacyverklaring van Chargee en in de Voorwaarden voor het delen van gegevens met Netbeheer Nederland.',
      ],
    },
    {
      id: 'stoppen',
      title: 'Zelf stoppen',
      blocks: [
        `Je kunt altijd stoppen via Mijn programma’s in ${PORTAL_NAME}.`,
        [
          'Stop je vóór de startdatum van je deelname, dan wordt je aanmelding direct geannuleerd. Je kunt je daarna opnieuw aanmelden zolang aanmelden mogelijk is.',
          'Stop je na de startdatum, dan eindigt je deelname op de eerste dag van de volgende kalendermaand. Tot die datum kun je je afmelding in Mijn programma’s nog terugdraaien en blijft artikel 5 gelden.',
        ],
        'Stop je na de startdatum, dan heb je geen recht op een Beloning, ook niet voor een deel van de Pilotperiode. Je kunt daarna niet opnieuw met dat Adres aan de Pilot deelnemen.',
        'Het intrekken van je toestemming voor het delen van gegevens met Netbeheer Nederland geldt als stoppen, met dezelfde gevolgen.',
      ],
    },
    {
      id: 'uitschrijving',
      title: 'Uitschrijving door Chargee',
      blocks: [
        'Chargee kan je deelname beëindigen als:',
        [
          'er tijdens het Piekvenster thuis is geladen;',
          'een of meer Apparaten langere tijd niet gekoppeld of niet verbonden zijn en je de verbinding na een melding van Chargee niet binnen een redelijke termijn herstelt;',
          'je niet (meer) voldoet aan artikel 3;',
          'er sprake is van misbruik zoals bedoeld in artikel 10.',
        ],
        'Bij uitschrijving laten we je weten waarom. De uitschrijving gaat in op de eerste dag van de volgende kalendermaand, behalve bij misbruik: dan gaat ze direct in. Na een uitschrijving heb je geen recht op een Beloning en kun je met dat Adres niet opnieuw deelnemen.',
        'Ben je het niet eens met een uitschrijving, dan kun je een klacht indienen zoals beschreven in de Algemene Voorwaarden. We beoordelen je klacht aan de hand van de gegevens van je Apparaten.',
      ],
    },
    {
      id: 'beloning',
      title: 'Beloning',
      blocks: [
        `Doe je mee tot het einde van de Pilotperiode en ben je niet gestopt of uitgeschreven, dan heb je recht op een eenmalige Beloning van € 5 per Adres voor elke kalendermaand van de Pilotperiode waarin je deelneemt, gerekend vanaf de kalendermaand waarin je deelname start. De Beloning is dus € 20 als je start in november 2026, € 15 bij een start in december 2026, € 10 bij een start in januari 2027 en € 5 bij een start in februari 2027. Het bedrag zie je bij je aanmelding en in ${PORTAL_NAME}.`,
        'De Beloning wordt na afloop van de Pilotperiode uitbetaald, nadat de netbeheerder de resultaten van de Pilot heeft bevestigd. Afhankelijk van je situatie betaalt je energieleverancier de Beloning uit, bijvoorbeeld door verrekening op je energierekening, of betaalt Chargee de Beloning uit. In Mijn programma’s zie je wie je Beloning uitbetaalt; het verwachte moment van uitbetaling laten we je weten.',
        'Een deelname waarbij een vermoeden van misbruik nog wordt onderzocht, wordt pas uitbetaald als het onderzoek is afgerond. Is een Beloning ten onrechte uitbetaald, dan mag Chargee die terugvorderen of verrekenen.',
        'De Beloning is niet overdraagbaar en kan niet worden ingewisseld voor iets anders.',
      ],
    },
    {
      id: 'misbruik',
      title: 'Misbruik',
      blocks: [
        'Het is niet toegestaan om de Pilot te misbruiken. Onder misbruik valt in ieder geval:',
        [
          'hetzelfde Adres meerdere keren aanmelden, bijvoorbeeld via een ander account, met een andere schrijfwijze of met een verzonnen toevoeging aan het huisnummer;',
          'dezelfde auto, laadpaal, Sparky of Flint voor meer dan één Adres laten meetellen;',
          'Apparaten lenen of tijdelijk koppelen alleen om aan de voorwaarden te voldoen;',
          'laden in het Piekvenster verbergen, bijvoorbeeld door Apparaten te ontkoppelen of uit te schakelen, of door met een niet gekoppelde laadpaal of auto te laden;',
          'onjuiste gegevens opgeven.',
        ],
        'Chargee controleert aanmeldingen op misbruik, onder meer door adresgegevens en meetgegevens van Apparaten te vergelijken met andere aanmeldingen. Een aanmelding kan daardoor worden geweigerd of handmatig worden beoordeeld. Bij misbruik kan Chargee je deelname direct beëindigen en een uitbetaalde Beloning terugvorderen.',
      ],
    },
    {
      id: 'aansprakelijkheid',
      title: 'Aansprakelijkheid',
      blocks: [
        'Deelname aan de Pilot is vrijwillig. Je bepaalt zelf hoe en wanneer je je auto laadt. Chargee is niet aansprakelijk voor schade doordat je auto in het Piekvenster niet is geladen, zoals een lagere acculading of een gemiste rit, of voor hogere energiekosten door laden op andere momenten.',
        'De aansprakelijkheid van Chargee in verband met de Pilot is beperkt tot het bedrag van de Beloning. Deze beperkingen gelden niet bij opzet of bewuste roekeloosheid van Chargee, en laten je rechten als consument op grond van de wet onverlet.',
      ],
    },
    {
      id: 'wijzigingen',
      title: 'Wijzigingen en einde van de Pilot',
      blocks: [
        `Chargee kan deze voorwaarden wijzigen. Een nieuwe revisie publiceren we in ${PORTAL_NAME} met de datum waarop ze ingaat. Bij een wijziging die nadelig voor je is, vragen we je opnieuw om akkoord; ga je niet akkoord, dan kun je stoppen zonder dat je eerder opgebouwde rechten verliest.`,
        'Chargee kan de Pilot eerder beëindigen of aanpassen als dat nodig is, bijvoorbeeld omdat de netbeheerder de Pilot stopt of omdat de wet- of regelgeving verandert. We laten je dat zo snel mogelijk weten en vertellen je dan hoe we met de Beloning omgaan.',
      ],
    },
    {
      id: 'recht-en-klachten',
      title: 'Klachten en toepasselijk recht',
      blocks: [
        `Heb je een vraag of klacht over de Pilot, neem dan contact op via ${C.support} of ${C.contact}. Voor klachten gelden de regels uit de Algemene Voorwaarden.`,
        'Op deze voorwaarden en je deelname is Nederlands recht van toepassing. Geschillen worden behandeld zoals beschreven in de Algemene Voorwaarden.',
      ],
    },
  ],
}
